import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { AdditionalFormType } from "../../interfaces/additionalFormType";
import {
  downloadBlankUserAgreementForm,
  getAdditionalFormTypes,
} from "../../services/additionalFormService";

interface DownloadClientFormProps {
  client: Client;
  onClose: (message?: string) => void;
}

const DownloadClientForm: React.FC<DownloadClientFormProps> = ({
  client,
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [additionalFormTypes, setAdditionalFormTypes] = useState<
    AdditionalFormType[] | null
  >(null);
  const [selectedFormType, setSelectedFormType] =
    useState<AdditionalFormType | null>(null);

  useEffect(() => {
    const getDetails = async () => {
      try {
        setLoadingSpinner(true);
        const additionalFormTypesResponse = await getAdditionalFormTypes();
        const filteredFormTypes = additionalFormTypesResponse.filter(
          (formType) =>
            formType.additionalFormTypeID !== 1 &&
            formType.additionalFormTypeID !== 5
        );

        setAdditionalFormTypes(filteredFormTypes);
      } catch (error) {
        console.error("Error getting additional form types:", error);
      } finally {
        setLoadingSpinner(false);
      }
    };

    getDetails();
  }, []);

  const handleDownloadForm = async () => {
    if (!selectedFormType) return;

    try {
      setLoadingSpinner(true);
      let response = null;

      switch (selectedFormType.additionalFormTypeID) {
        case 2:
          response =
            "https://oxfordstoreacctdev.blob.core.windows.net/documents/Credit Required Forms.pdf";
          break;
        case 3:
          response =
            "https://oxfordstoreacctdev.blob.core.windows.net/documents/SSN Required Forms.pdf";
          break;
        case 4:
          response = await downloadBlankUserAgreementForm();
          if (response) {
            const blob = await response.blob();
            response = window.URL.createObjectURL(blob);
          }
          break;
        default:
          throw new Error("Invalid form type selected");
      }

      if (!response) throw new Error("No response from server");

      const newWindow = window.open(response, "_blank");

      if (!newWindow) {
        console.error(
          "Failed to open new window. It may be blocked by a popup blocker."
        );
      }

      return true;
    } catch (error) {
      console.error("Error handling form download:", error);
      setErrorMessage("Error downloading candidate form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Download Blank Client Form</h2>
          <div>Please choose a form type.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="form-group">
            <select
              id="formTypeSelect"
              className="form-control"
              value={selectedFormType?.description || ""}
              onChange={(e) => {
                const selected = additionalFormTypes?.find(
                  (type) => type.description === e.target.value
                );
                setSelectedFormType(selected || null);
              }}
            >
              <option value="" disabled>
                -- Select Form Type --
              </option>
              {additionalFormTypes?.map((formType) => (
                <option
                  key={formType.additionalFormTypeID}
                  value={formType.description}
                >
                  {formType.description}
                </option>
              ))}
            </select>
          </div>
          <div className="action-buttons">
            <button
              className="smbtn btn-grey"
              onClick={() => handleDownloadForm()}
            >
              Download Client Form
            </button>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default DownloadClientForm;
